import React, { useMemo, useState } from "react";
import { useTranslation } from "react-i18next";
import styled from "styled-components";

import { Drawer } from "../../../components/Drawer";
import { Rounded } from "../../../theme/Rounded";
import { Spacing } from "../../../theme/Spacing";
import { Flex } from "../../../components/Flex";
import { AtiraIcon } from "../../../components/AtiraIcon";
import { Text } from "../../../components/Text";
import { Radio } from "../../../utils/local-antd";
import { getKindColorKey, getKindIcon } from "../../../utils/Reports";
import { ReportsUserSummaryGeneralTab } from "./tabs/user-summary/ReportsUserSummaryGeneralTab";
import { GetReportsResponseDto } from "../../../model/reports/dto/GetReportsResponseDto";
import { ActivityEntityKind } from "../../../model/activity/types/ActivityEntityKind.enum";

const StatsRow = styled(Flex)`
  gap: ${Spacing.l};
  flex-wrap: wrap;
  padding-bottom: ${Spacing.m};
  border-bottom: 1px solid ${({ theme }) => theme.darkSub};
`;

const StatCell = styled(Flex)`
  flex-direction: column;
  min-width: 8rem;
`;

// Order controls both the filter chips and the merged/sorted activity feed's
// tie-break, so it stays fixed rather than following whatever order the API
// happened to return `byEntity`'s keys in.
const KIND_ORDER = [
  ActivityEntityKind.ENTRY,
  ActivityEntityKind.KANBAN_CARD,
  ActivityEntityKind.TASK,
  ActivityEntityKind.FORM,
  ActivityEntityKind.MODULE,
];

/**
 * Independent, fully-rounded pills with a real gap between them, rather than
 * antd's default fused button-group (adjacent buttons sharing one border via
 * an absolutely-positioned divider). That divider is built assuming
 * left-to-right neighbours; in an RTL layout it lands on the wrong edge and
 * shows up as a broken line between two chips. Disabling it and giving every
 * button its own full border sidesteps the direction bug entirely instead of
 * trying to mirror antd's internal math.
 */
const StyledRadioGroup = styled(Radio.Group)`
  display: flex;
  flex-wrap: wrap;
  gap: ${Spacing.xs};

  .ant-radio-button-wrapper {
    display: inline-flex;
    align-items: center;
    height: auto;
    padding-block: ${Spacing.xs};
    border-inline-start-width: 1px !important;
    border-radius: ${Rounded.md} !important;
  }

  .ant-radio-button-wrapper::before {
    display: none !important;
  }

  .ant-radio-button-wrapper-checked:not(.ant-radio-button-wrapper-disabled) {
    background: ${({ theme }) => theme.main} !important;
    border-color: ${({ theme }) => theme.main} !important;
    color: ${({ theme }) => theme.white} !important;
  }
`;

const ChipLabel = styled(Flex)`
  align-items: center;
  gap: ${Spacing.xs};
`;

// "Deals" here, not `getKindLabel`'s generic "Deal Card" - matches the wording
// the Reports tabs and the table this drawer opened from already use.
const KIND_LABEL_KEY: Partial<Record<ActivityEntityKind, string>> = {
  [ActivityEntityKind.ENTRY]: "common.entries",
  [ActivityEntityKind.KANBAN_CARD]: "common.deals",
  [ActivityEntityKind.TASK]: "common.tasks",
  [ActivityEntityKind.FORM]: "common.forms",
  [ActivityEntityKind.MODULE]: "common.modules",
};

type Props = {
  open: boolean;
  setOpen: ATVoidFunction<boolean>;
  data?: GetReportsResponseDto["metricsDataCentric"][number];
  /**
   * The same "closed" figures the member's row shows in the table this drawer
   * was opened from - shown up top so the number that brought someone here is
   * never more than a glance away from the fuller activity list below it,
   * which is deliberately broader (every action, not just closes).
   */
  metrics?: {
    dealsClosed: number;
    tasksDone: number;
    entriesConverted: number;
  };
};

export const ReportsUserDrawer: React.FC<Props> = ({
  open,
  setOpen,
  data,
  metrics,
}) => {
  const { t } = useTranslation();
  const [kindFilter, setKindFilter] = useState<ActivityEntityKind | "ALL">(
    "ALL",
  );

  const items = useMemo(() => {
    const merged = KIND_ORDER.flatMap((kind) => data?.byEntity?.[kind] || []);

    return merged
      .slice()
      .sort(
        (a, b) =>
          new Date(b.latestCreatedAt).getTime() -
          new Date(a.latestCreatedAt).getTime(),
      );
  }, [data]);

  const filteredItems = useMemo(
    () =>
      kindFilter === "ALL"
        ? items
        : items.filter((item) => item.entity?.entityKind === kindFilter),
    [items, kindFilter],
  );

  const kindOptions = [
    {
      label: <ChipLabel>{`${t("common.all")} (${items.length})`}</ChipLabel>,
      value: "ALL" as const,
    },
    ...KIND_ORDER.filter((kind) => data?.byEntity?.[kind]?.length).map(
      (kind) => {
        const selected = kindFilter === kind;

        return {
          label: (
            <ChipLabel>
              <AtiraIcon
                icon={getKindIcon(kind)}
                color={selected ? "white" : getKindColorKey(kind)}
              />
              {`${t(KIND_LABEL_KEY[kind]!)} (${data?.byEntity?.[kind]?.length ?? 0})`}
            </ChipLabel>
          ),
          value: kind,
        };
      },
    ),
  ];

  return (
    <Drawer
      title={t("reports.tabs.summary.subtab.user.drawer.title", {
        name: data?.name,
      })}
      open={open}
      onClose={() => setOpen(false)}
      size="large"
    >
      <Flex flexDirection="column" gap="m">
        {metrics ? (
          <Flex flexDirection="column" gap="xs">
            <StatsRow>
              <StatCell>
                <Text fontSize="s" color="darkerSub">
                  {t("common.entriesConvertedByUser")}
                </Text>
                <Text fontSize="l" fontWeight="bold">
                  {metrics.entriesConverted}
                </Text>
              </StatCell>
              <StatCell>
                <Text fontSize="s" color="darkerSub">
                  {t("common.dealsClosedByUser")}
                </Text>
                <Text fontSize="l" fontWeight="bold">
                  {metrics.dealsClosed}
                </Text>
              </StatCell>
              <StatCell>
                <Text fontSize="s" color="darkerSub">
                  {t("common.tasksClosedByUsers")}
                </Text>
                <Text fontSize="l" fontWeight="bold">
                  {metrics.tasksDone}
                </Text>
              </StatCell>
            </StatsRow>
            <Text fontSize="s" color="darkerSub">
              {t("reports.tabs.summary.subtab.user.drawer.hint")}
            </Text>
          </Flex>
        ) : null}

        <StyledRadioGroup
          value={kindFilter}
          onChange={(e) => setKindFilter(e.target.value)}
          optionType="button"
          options={kindOptions}
        />

        <Flex flexDirection="column" paddingBottom="xl">
          <ReportsUserSummaryGeneralTab data={filteredItems} />
        </Flex>
      </Flex>
    </Drawer>
  );
};
